import type {
  AdminSampleOfferInput,
  AdminSampleOrderAction,
  AdminSampleOrderView,
  FactorySampleOfferInput,
  FactorySampleOrderView,
  SampleCategorySummary,
  SampleFactoryOption,
  SampleOfferView,
  SampleProgressInput,
  AdminSampleMessageAction,
  AdminSampleMessageView,
  SampleMessageView,
  SendSampleMessageInput,
} from '@repo/shared'
import { json, request } from './api'
import { env } from './env'
import type { GetToken } from './lib/auth'

/** 주문 감사 기록 1행 (GET /admin/sample-orders/:id) */
export interface SampleOrderEventView {
  id: string
  kind: string
  actorRole: string
  actorId: string
  detail: Record<string, unknown>
  createdAt: string
}

/**
 * 샘플 거래 API (AI 중계 샘플, docs/design-ai-brokered-samples.md). 관리자([샘플 거래])와 공장 포털이 같이 쓴다.
 * api.ts 를 덜 고치려고 따로 둔다
 */
export const samplesApi = {
  categories: (getToken: GetToken) =>
    request<{ items: SampleCategorySummary[] }>(getToken, '/admin/sample-categories'),
  offers: (getToken: GetToken) =>
    request<{ items: SampleOfferView[] }>(getToken, '/admin/sample-offers'),
  factories: (getToken: GetToken) =>
    request<{ items: SampleFactoryOption[] }>(getToken, '/admin/sample-factories'),
  saveOffer: (getToken: GetToken, input: AdminSampleOfferInput, id?: string) =>
    request<{ offer: SampleOfferView }>(
      getToken,
      id ? `/admin/sample-offers/${encodeURIComponent(id)}` : '/admin/sample-offers',
      json(id ? 'PUT' : 'POST', input),
    ),
  orders: (getToken: GetToken) =>
    request<{ items: AdminSampleOrderView[] }>(getToken, '/admin/sample-orders'),
  order: (getToken: GetToken, id: string) =>
    request<{
      order: AdminSampleOrderView
      factoryView: FactorySampleOrderView
      events: SampleOrderEventView[]
    }>(getToken, `/admin/sample-orders/${encodeURIComponent(id)}`),
  adminProgress: (getToken: GetToken, id: string, input: SampleProgressInput) =>
    request<{ ok: true }>(
      getToken,
      `/admin/sample-orders/${encodeURIComponent(id)}/progress`,
      json('POST', input),
    ),
  action: (getToken: GetToken, id: string, input: AdminSampleOrderAction) =>
    request<{ order: AdminSampleOrderView }>(
      getToken,
      `/admin/sample-orders/${encodeURIComponent(id)}/action`,
      json('POST', input),
    ),

  messageQueue: (getToken: GetToken) =>
    request<{ items: AdminSampleMessageView[] }>(getToken, '/admin/sample-messages'),
  orderMessages: (getToken: GetToken, id: string) =>
    request<{ items: AdminSampleMessageView[] }>(
      getToken,
      `/admin/sample-orders/${encodeURIComponent(id)}/messages`,
    ),
  messageAction: (getToken: GetToken, id: string, input: AdminSampleMessageAction) =>
    request<{ message: AdminSampleMessageView }>(
      getToken,
      `/admin/sample-messages/${encodeURIComponent(id)}/action`,
      json('POST', input),
    ),

  // ── 공장 포털 ──
  factoryOffers: (getToken: GetToken) =>
    request<{ items: SampleOfferView[] }>(getToken, '/factory-samples/offers'),
  factorySaveOffer: (getToken: GetToken, input: FactorySampleOfferInput, id?: string) =>
    request<{ offer: SampleOfferView }>(
      getToken,
      id ? `/factory-samples/offers/${encodeURIComponent(id)}` : '/factory-samples/offers',
      json(id ? 'PUT' : 'POST', input),
    ),
  factoryOrders: (getToken: GetToken) =>
    request<{ items: FactorySampleOrderView[] }>(getToken, '/factory-samples/orders'),
  factoryProgress: (getToken: GetToken, id: string, input: SampleProgressInput) =>
    request<{ order: FactorySampleOrderView }>(
      getToken,
      `/factory-samples/orders/${encodeURIComponent(id)}/progress`,
      json('POST', input),
    ),
  factoryMessages: (getToken: GetToken, id: string) =>
    request<{ items: SampleMessageView[] }>(
      getToken,
      `/factory-samples/orders/${encodeURIComponent(id)}/messages`,
    ),
  factorySendMessage: (getToken: GetToken, id: string, input: SendSampleMessageInput) =>
    request<{ items: SampleMessageView[] }>(
      getToken,
      `/factory-samples/orders/${encodeURIComponent(id)}/messages`,
      json('POST', input),
    ),
  /** 사양서 CSV (서버가 만든다 — 화면·JSON 과 같은 객체) */
  factorySpecCsv: async (getToken: GetToken, id: string): Promise<Blob> => {
    const token = await getToken()
    const res = await fetch(
      `${env.API_URL}/factory-samples/orders/${encodeURIComponent(id)}/spec.csv`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    return res.blob()
  },
}

/** 브라우저에서 파일로 내려받기 */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
